import { useContext } from 'react';
import useSWR from 'swr';
import fetcher from '@utils/fetcher';
import streamingFetcher from '../../utils/streamingFetcher';
import getCurrentYear from '../../utils/timeUtils';
import { SiteInfoContext } from '@contexts/SiteInfoContext';

const SABA_URL = 'https://wonderfulnextapi.com/api';

const refreshInterval = 60 * 1000;
const nowYear = getCurrentYear();

// token 由 server 端 process.env（Vault 注入）經 pageProps 帶進 SiteInfoContext
const useLiveScoreTokens = () => {
    const { envConfig } = useContext(SiteInfoContext);
    return {
        keyToken: envConfig?.liveScoreKeyToken || '',
        bearerToken: envConfig?.liveScoreBearerToken ? `Bearer ${envConfig.liveScoreBearerToken}` : '',
    };
};

const useTodayMatch = (
    isOnTodayTab,
    supportedLanguages,
    imageDomain,
    versionNumber = '1.0',
) => {
    const { keyToken } = useLiveScoreTokens();
    const { data: basketballToday, error: basketballError } = useSWR(
        () => (isOnTodayTab ? [
            `${SABA_URL}/${versionNumber}/Match/Basketball/Today/`,
            {
                method: 'POST',
                headers: { key: keyToken },
            },
        ] : null),
        fetcher,
    );

    const { data: soccerToday, error: soccerError } = useSWR(
        () => (isOnTodayTab ? [
            `${SABA_URL}/${versionNumber}/Match/Soccer/Today/`,
            {
                method: 'POST',
                headers: { key: keyToken },
            },
        ] : null),
        fetcher,
    );

    const { data: tennisToday, error: tennisError } = useSWR(
        () => (isOnTodayTab ? [
            `${SABA_URL}/${versionNumber}/Match/Tennis/Today/`,
            {
                method: 'POST',
                headers: { key: keyToken },
            },
        ] : null),
        fetcher,
    );

    const { data: baseballToday, error: baseballError } = useSWR(
        () => (isOnTodayTab ? [
            `${SABA_URL}/${versionNumber}/Match/Baseball/Today/`,
            {
                method: 'POST',
                headers: { key: keyToken },
            },
        ] : null),
        fetcher,
    );

    const isLoading = (!basketballToday && !basketballError)
        // || (!cricketToday && !cricketError)
        || (!soccerToday && !soccerError)
        || (!tennisToday && !tennisError)
        || (!baseballToday && !baseballError);

    return {
        isLoading,
        basketballToday,
        // cricketToday,
        soccerToday,
        tennisToday,
        baseballToday,
    };
};

const useSoccerToday = (
    isOnTodayTab = true,
    versionNumber = '1.0',
) => {
    const { keyToken, bearerToken } = useLiveScoreTokens();
    const { data: soccerToday, error: soccerError } = useSWR(
        () => (isOnTodayTab ? [
            `${SABA_URL}/${versionNumber}/Match/Soccer/Today/`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    key: keyToken,
                    Authorization: bearerToken,
                },
            },
        ] : null),
        fetcher,
    );

    const isLoading = (!soccerToday && !soccerError);
    return {
        isLoading,
        soccerToday,
    };
};

export const getSoccerTodayMatchInfos = (
    isOnTodayTab = true,
) => {
    const { clientApiUrl } = useContext(SiteInfoContext);

    const { data: soccerTodayMatchInfos, error } = useSWR(
        () => (isOnTodayTab ? {
            url: clientApiUrl+ '/api/SabaSport/GetSoccerTodayMatchInfos',
            options: null,
            isToday: true,
        } : null),
        streamingFetcher,
        {
            revalidateOnFocus: false,
            refreshInterval,
        },
    );

    const isLoading = (!soccerTodayMatchInfos && !error);
    return {
        isLoading,
        soccerTodayMatchInfos,
    };
};

const useBasketballToday = (
    isOnTodayTab = true,
    versionNumber = '1.0',
) => {
    const { keyToken, bearerToken } = useLiveScoreTokens();
    const { data: basketballToday, error: basketballError } = useSWR(
        () => (isOnTodayTab ? [
            `${SABA_URL}/${versionNumber}/Match/Basketball/Today/`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    key: keyToken,
                    Authorization: bearerToken,
                },
            },
        ] : null),
        fetcher,
    );

    const isLoading = (!basketballToday && !basketballError);
    return {
        isLoading,
        basketballToday,
    };
};

export const getBasketballTodayMatchInfos = (
    isOnTodayTab = true,
) => {
    const { clientApiUrl } = useContext(SiteInfoContext);
    const { data: basketballTodayMatchInfos, error } = useSWR(
        () => (isOnTodayTab ? {
            url: clientApiUrl + '/api/SabaSport/GetBasketballTodayMatchInfos',
            options: null,
            isToday: true,
        } : null),
        streamingFetcher,
        {
            revalidateOnFocus: false,
            refreshInterval,
        },
    );

    const isLoading = (!basketballTodayMatchInfos && !error);
    return {
        isLoading,
        basketballTodayMatchInfos,
    };
};

const useCricketToday = (
    isOnTodayTab = true,
    versionNumber = '1.0',
) => {
    const { keyToken, bearerToken } = useLiveScoreTokens();
    const { data: cricketToday, error: cricketError } = useSWR(
        () => (isOnTodayTab ? [
            `${SABA_URL}/${versionNumber}/Match/Cricket/Today/`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    key: keyToken,
                    Authorization: bearerToken,
                },
            },
        ] : null),
        fetcher,
    );

    const isLoading = (!cricketToday && !cricketError);
    return {
        isLoading,
        cricketToday,
    };
};

export const getCricketTodayMatchInfos = (
    isOnTodayTab = true,
) => {
    const { clientApiUrl } = useContext(SiteInfoContext);
    const { data: cricketTodayMatchInfos, error } = useSWR(
        () => (isOnTodayTab ? {
            url: clientApiUrl + '/api/SabaSport/GetCricketTodayMatchInfos',
            options: null,
            isToday: true,
        } : null),
        streamingFetcher,
        {
            revalidateOnFocus: false,
            refreshInterval,
        },
    );

    const isLoading = (!cricketTodayMatchInfos && !error);
    return {
        isLoading,
        cricketTodayMatchInfos,
    };
};

const useResult = (
    month,
    isOnTodayTab,
    date,
    versionNumber = '1.0',
) => {
    const { keyToken, bearerToken } = useLiveScoreTokens();
    const { data: soccerResult, error: soccerError } = useSWR(
        () => (!isOnTodayTab && date ? [
            `${SABA_URL}/${versionNumber}/Match/Soccer/Date`,
            {
                method: 'POST',
                body: JSON.stringify({ Date: `${nowYear}-${month}-${date}` }),
                headers: {
                    'Content-Type': 'application/json',
                    key: keyToken,
                    Authorization: bearerToken,
                },
            },
        ] : null),
        fetcher,
    );

    const { data: basketballResult, error: basketballError } = useSWR(
        () => (!isOnTodayTab && date ? [
            `${SABA_URL}/${versionNumber}/Match/Basketball/Date`,
            {
                method: 'POST',
                body: JSON.stringify({ Date: `${nowYear}-${month}-${date}` }),
                headers: { key: keyToken, 'Content-Type': 'application/json' },
            },
        ] : null),
        fetcher,
    );

    const isResultLoading = !basketballResult && !basketballError;

    return { soccerResult, basketballResult, isResultLoading };
};

const useSoccerResult = (
    month,
    date,
    versionNumber = '1.0',
) => {
    const { keyToken, bearerToken } = useLiveScoreTokens();
    const { data: soccerResult, error: soccerError } = useSWR(
        () => (date
            ? {
                url: `${SABA_URL}/${versionNumber}/Match/Soccer/Date`,
                options: {
                    method: 'POST',
                    body: JSON.stringify({ Date: `${nowYear}-${month}-${date}` }),
                    headers: {
                        key: keyToken,
                        'Content-Type': 'application/json',
                        Authorization: bearerToken,
                    },
                },
                isToday: false,
            } : null),
        streamingFetcher,
        {
            revalidateOnFocus: false,
            refreshInterval,
        },
    );

    const isResultLoading = !soccerResult && !soccerError;

    return { soccerResult, isResultLoading };
};

const useBasketballResult = (
    month,
    date,
    versionNumber = '1.0',
) => {
    const { keyToken, bearerToken } = useLiveScoreTokens();
    const { data: basketballResult, error: basketballError } = useSWR(
        () => (date ? {
            url: `${SABA_URL}/${versionNumber}/Match/Basketball/Date`,
            options: {
                method: 'POST',
                body: JSON.stringify({ Date: `${nowYear}-${month}-${date}` }),
                headers: {
                    key: keyToken,
                    'Content-Type': 'application/json',
                    Authorization: bearerToken,
                },
            },
            isToday: false,
        } : null),
        streamingFetcher,
        {
            revalidateOnFocus: false,
            refreshInterval,
        },
    );

    const isResultLoading = !basketballResult && !basketballError;

    return { basketballResult, isResultLoading };
};

const useCricketResult = (
    month,
    date,
    versionNumber = '1.0',
) => {
    const { keyToken, bearerToken } = useLiveScoreTokens();
    const { data: cricketResult, error: cricketError } = useSWR(
        () => (date ? {
            url: `${SABA_URL}/${versionNumber}/Match/Cricket/Date`,
            options: {
                method: 'POST',
                body: JSON.stringify({ Date: `${nowYear}-${month}-${date}` }),
                headers: {
                    key: keyToken,
                    'Content-Type': 'application/json',
                    Authorization: bearerToken,
                },
            },
            isToday: false,
        } : null),
        streamingFetcher,
        {
            revalidateOnFocus: false,
            refreshInterval,
        },
    );

    const isResultLoading = !cricketResult && !cricketError;

    return { cricketResult, isResultLoading };
};

export {
    useSoccerToday,
    useBasketballToday,
    useCricketToday,
    useTodayMatch,
    useResult,
    useSoccerResult,
    useBasketballResult,
    useCricketResult,
};
